import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tab,
  Tabs,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  LinearProgress,
  Link,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { api } from '../services/api-client';
import type {
  PipelineRun,
  PipelineRunStatus,
  PipelineConfig,
  PipelineEnvironment,
  PipelineStageName,
  PipelineStage,
  CreatePipelineRunRequest,
} from '../services/api-client';
import PipelineStageStepper from '../components/PipelineStageStepper';
import NewRunDialog from '../components/NewRunDialog';

// -----------------------------------------------------------------------------
// Status badge helpers
// -----------------------------------------------------------------------------

const STATUS_COLORS: Record<PipelineRunStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  running: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

function StatusChip({ status }: { status: PipelineRunStatus }) {
  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      color={STATUS_COLORS[status] || 'default'}
      size="small"
      variant={status === 'running' ? 'filled' : 'outlined'}
    />
  );
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, HH:mm');
  } catch {
    return dateStr;
  }
}

// =============================================================================
// Runs Tab
// =============================================================================

function RunsTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [envFilter, setEnvFilter] = useState<string>('');

  const { data, isLoading, error } = useQuery(
    ['pipeline-runs', statusFilter, envFilter],
    () =>
      api.getPipelineRuns({
        limit: 30,
        ...(statusFilter ? { status: statusFilter as PipelineRunStatus } : {}),
        ...(envFilter ? { environment: envFilter as PipelineEnvironment } : {}),
      }),
    {
      refetchInterval: selectedRunId ? false : 15000,
      enabled: !selectedRunId,
    }
  );

  const createMutation = useMutation(
    (params: CreatePipelineRunRequest) => api.createPipelineRun(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pipeline-runs');
        setDialogOpen(false);
      },
    }
  );

  const cancelMutation = useMutation(
    (runId: string) => api.cancelPipelineRun(runId),
    { onSuccess: () => queryClient.invalidateQueries('pipeline-runs') }
  );

  // If a run is selected, show detail view
  if (selectedRunId) {
    return <RunDetail runId={selectedRunId} onBack={() => setSelectedRunId(null)} />;
  }

  const runs = data?.runs || [];

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Environment</InputLabel>
            <Select value={envFilter} label="Environment" onChange={(e) => setEnvFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="staging">Staging</MenuItem>
              <MenuItem value="prod">Prod</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries('pipeline-runs')} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New Run
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {'Failed to load pipeline runs: ' + String((error as Error)?.message || error)}
        </Alert>
      ) : null}
      {createMutation.error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => createMutation.reset()}>
          {String((createMutation.error as Error)?.message || createMutation.error)}
        </Alert>
      ) : null}

      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : runs.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              No pipeline runs found. Click "New Run" to start one.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run ID</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Config</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run: PipelineRun) => (
                <TableRow key={run.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedRunId(run.run_id || run.id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.8rem">
                      {run.run_id || run.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={run.environment}
                      size="small"
                      color={run.environment === 'prod' ? 'error' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={run.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {run.trigger_type === 'scheduled' ? 'Scheduled' : run.triggered_by || 'Manual'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(run.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {run.config?.limit && (
                        <Chip label={`Limit: ${run.config.limit}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {run.config?.country_allowlist && (
                        <Chip label="Country filter" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {run.config?.image_type && run.config.image_type !== 'all' && (
                        <Chip label={`Img: ${run.config.image_type}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setSelectedRunId(run.run_id || run.id)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {run.status === 'running' && (
                        <Tooltip title="Cancel run">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (window.confirm(`Cancel run ${run.run_id}?`)) {
                                cancelMutation.mutate(run.run_id || run.id);
                              }
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <NewRunDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={(params) => createMutation.mutate(params)}
        loading={createMutation.isLoading}
      />
    </Box>
  );
}

// =============================================================================
// Run Detail View
// =============================================================================

function RunDetail({ runId, onBack }: { runId: string; onBack: () => void }) {
  const queryClient = useQueryClient();

  const { data: progress, isLoading, error } = useQuery(
    ['pipeline-progress', runId],
    () => api.getPipelineProgress(runId),
    {
      refetchInterval: (data) =>
        data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled'
          ? false
          : 8000,
    }
  );

  const triggerMutation = useMutation(
    ({ stage }: { stage: PipelineStageName }) => api.triggerPipelineStage(runId, stage),
    { onSuccess: () => queryClient.invalidateQueries(['pipeline-progress', runId]) }
  );

  const cancelMutation = useMutation(
    () => api.cancelPipelineRun(runId),
    { onSuccess: () => queryClient.invalidateQueries(['pipeline-progress', runId]) }
  );

  const stages = progress?.stages;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h6" fontFamily="monospace" fontSize="1rem">
          {runId}
        </Typography>
        {progress?.status && <StatusChip status={progress.status} />}
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String((error as Error)?.message || error)}
        </Alert>
      ) : null}

      {stages && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Pipeline Stages
            </Typography>
            <PipelineStageStepper stages={stages} />
          </CardContent>
        </Card>
      )}

      {/* Stage Progress */}
      {stages && (() => {
        const stageEntries = (Object.entries(stages) as [PipelineStageName, PipelineStage][])
          .filter(([, s]) => s.checkpoint || s.summary);
        if (stageEntries.length === 0) return null;
        return (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Stage Progress
              </Typography>
              {stageEntries.map(([name, stage]) => {
                const data = stage.summary || stage.checkpoint;
                if (!data) return null;
                const total = data.total;
                const processed = data.processed;
                const skipped = data.skipped_unchanged;
                const failed = stage.summary
                  ? (stage.summary.ers_failed ?? 0) + (stage.summary.errors ?? 0)
                  : data.ers_failed;
                const rate = stage.summary?.rate_per_s;
                const pct = total && processed != null ? Math.min((processed / total) * 100, 100) : null;

                return (
                  <Box key={name} sx={{ mb: stageEntries.length > 1 ? 2.5 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {name.replace('_', ' ')}
                      </Typography>
                      {stage.log_url && (
                        <Link
                          href={stage.log_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          underline="hover"
                        >
                          View Logs
                        </Link>
                      )}
                    </Box>
                    {pct != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>
                          {Math.round(pct)}%
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {total != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Hotels</Typography>
                          <Typography variant="body2">{Number(total).toLocaleString()}</Typography>
                        </Grid>
                      )}
                      {processed != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Processed</Typography>
                          <Typography variant="body2">{Number(processed).toLocaleString()}</Typography>
                        </Grid>
                      )}
                      {skipped != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Skipped (unchanged)</Typography>
                          <Typography variant="body2">{Number(skipped).toLocaleString()}</Typography>
                        </Grid>
                      )}
                      {failed != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Failed / Errors</Typography>
                          <Typography variant="body2" color={failed > 0 ? 'error.main' : undefined}>
                            {Number(failed).toLocaleString()}
                          </Typography>
                        </Grid>
                      )}
                      {rate != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Rate</Typography>
                          <Typography variant="body2">{rate.toFixed(1)}/s</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        );
      })()}

      {/* Stage actions */}
      {progress?.status !== 'cancelled' && progress?.status !== 'failed' && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Stage Controls
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Manually trigger individual stages for this run.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(['scan', 'process', 'image_download', 'image_embed'] as PipelineStageName[]).map((stage) => {
                const stageData = stages?.[stage];
                const canTrigger = !stageData || stageData.status === 'pending' || stageData.status === 'failed';
                return (
                  <Button
                    key={stage}
                    variant="outlined"
                    size="small"
                    startIcon={<PlayIcon />}
                    disabled={!canTrigger || triggerMutation.isLoading}
                    onClick={() => triggerMutation.mutate({ stage })}
                  >
                    {stage.replace('_', ' ')}
                  </Button>
                );
              })}
            </Box>
            {triggerMutation.error ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {String((triggerMutation.error as Error)?.message || triggerMutation.error)}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Run config summary */}
      {progress?.config && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Run Configuration
            </Typography>
            <Grid container spacing={2}>
              {progress.config.limit && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Hotel Limit</Typography>
                  <Typography variant="body2">{Number(progress.config.limit).toLocaleString()}</Typography>
                </Grid>
              )}
              {progress.config.country_allowlist && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Countries</Typography>
                  <Typography variant="body2" fontSize="0.75rem">{progress.config.country_allowlist}</Typography>
                </Grid>
              )}
              {progress.config.image_type && progress.config.image_type !== 'all' && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Image Type</Typography>
                  <Typography variant="body2">{progress.config.image_type}</Typography>
                </Grid>
              )}
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Elapsed</Typography>
                <Typography variant="body2">
                  {progress.elapsed_seconds ? `${Math.floor(progress.elapsed_seconds / 60)}m` : '—'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Cancel button */}
      {progress?.status === 'running' && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => {
              if (window.confirm('Cancel this pipeline run?')) {
                cancelMutation.mutate();
              }
            }}
            disabled={cancelMutation.isLoading}
          >
            Cancel Run
          </Button>
        </Box>
      )}
    </Box>
  );
}

// =============================================================================
// Config Tab
// =============================================================================

function ConfigTab() {
  const queryClient = useQueryClient();
  const [localConfig, setLocalConfig] = useState<Partial<PipelineConfig>>({});
  const [dirty, setDirty] = useState(false);

  const { data: config, isLoading, error } = useQuery('pipeline-config', () => api.getPipelineConfig(), {
    onSuccess: (data) => {
      setLocalConfig(data);
      setDirty(false);
    },
  });

  const saveMutation = useMutation(
    (updates: Partial<PipelineConfig>) => api.updatePipelineConfig(updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pipeline-config');
        setDirty(false);
      },
    }
  );

  const updateField = <K extends keyof PipelineConfig>(key: K, value: PipelineConfig[K]) => {
    setLocalConfig((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load config: {String((error as Error)?.message || error)}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Pipeline Defaults & Scheduling
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!dirty || saveMutation.isLoading}
          onClick={() => saveMutation.mutate(localConfig)}
        >
          {saveMutation.isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {saveMutation.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String((saveMutation.error as Error)?.message || saveMutation.error)}
        </Alert>
      ) : null}
      {saveMutation.isSuccess && !dirty && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Configuration saved.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Scheduling */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Automated Schedule
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={localConfig.schedule_enabled ?? false}
                    onChange={(e) => updateField('schedule_enabled', e.target.checked)}
                  />
                }
                label="Enable scheduled runs"
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, mb: 1, display: 'block' }}>
                Run on these days (UTC)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => {
                  const days = localConfig.schedule_days ?? [1];
                  const selected = days.includes(idx);
                  return (
                    <Chip
                      key={day}
                      label={day}
                      size="small"
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      disabled={!localConfig.schedule_enabled}
                      onClick={() => {
                        const next = selected ? days.filter((d: number) => d !== idx) : [...days, idx].sort();
                        if (next.length > 0) updateField('schedule_days', next);
                      }}
                    />
                  );
                })}
              </Box>

              <TextField
                label="Time (UTC)"
                size="small"
                type="time"
                fullWidth
                value={localConfig.schedule_time ?? '02:00'}
                onChange={(e) => updateField('schedule_time', e.target.value)}
                disabled={!localConfig.schedule_enabled}
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Schedule Environment</InputLabel>
                <Select
                  value={localConfig.schedule_environment ?? 'prod'}
                  label="Schedule Environment"
                  onChange={(e) => updateField('schedule_environment', e.target.value as PipelineEnvironment)}
                  disabled={!localConfig.schedule_enabled}
                >
                  <MenuItem value="staging">Staging</MenuItem>
                  <MenuItem value="prod">Production</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Default Config */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Default Run Configuration
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                These defaults apply when no preset or override is specified.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Default Limit"
                    size="small"
                    fullWidth
                    value={localConfig.default_config?.limit ?? ''}
                    onChange={(e) =>
                      updateField('default_config', {
                        ...localConfig.default_config,
                        limit: e.target.value,
                      })
                    }
                    placeholder="No limit"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Scan Tasks"
                    size="small"
                    fullWidth
                    type="number"
                    value={localConfig.default_config?.scan_tasks ?? 64}
                    onChange={(e) =>
                      updateField('default_config', {
                        ...localConfig.default_config,
                        scan_tasks: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Process Tasks"
                    size="small"
                    fullWidth
                    type="number"
                    value={localConfig.default_config?.process_tasks ?? 32}
                    onChange={(e) =>
                      updateField('default_config', {
                        ...localConfig.default_config,
                        process_tasks: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Image DL Tasks"
                    size="small"
                    fullWidth
                    type="number"
                    value={localConfig.default_config?.img_dl_tasks ?? 16}
                    onChange={(e) =>
                      updateField('default_config', {
                        ...localConfig.default_config,
                        img_dl_tasks: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Info about last update */}
        {config?.updated_at && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatDate(config.updated_at)}
              {config.updated_by ? ` by ${config.updated_by}` : ''}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export default function PipelineManagement() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Pipeline Management
        </Typography>
      </Box>

      <Card variant="outlined">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Runs" />
          <Tab label="Configuration" />
        </Tabs>
        <CardContent sx={{ pt: 3 }}>
          {tab === 0 && <RunsTab />}
          {tab === 1 && <ConfigTab />}
        </CardContent>
      </Card>
    </Box>
  );
}
